import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { isIos, isStandalone } from "./usePwaEnv";

/**
 * `InstallPrompt` — a dismissible "install app" (Add-to-Home-Screen) card. [task #15]
 *
 * On Chromium browsers it captures the `beforeinstallprompt` event, suppresses
 * the mini-infobar, and surfaces a branded card whose button calls `prompt()`.
 * On iOS Safari (no `beforeinstallprompt`) it shows a one-time textual hint
 * instead. The card hides when the app is already installed (standalone) or the
 * user previously dismissed it (persisted in `localStorage`).
 *
 * Reduced-motion: the entrance is `motion-safe:` only. Storybook-safe: all
 * browser access happens inside effects; the static render is `null`.
 */

const DISMISS_KEY = "sij_pwa_install_dismissed";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function persistDismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* private mode / storage disabled — non-fatal */
  }
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHint, setIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || wasDismissed()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // suppress the default mini-infobar
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      persistDismiss();
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS has no beforeinstallprompt — offer the manual hint once.
    if (isIos()) {
      setIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible) return null;

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") persistDismiss();
    setDeferred(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    persistDismiss();
    setVisible(false);
  };

  return (
    <div
      data-testid="install-prompt"
      role="dialog"
      aria-label={t("pwa.installTitle")}
      className={cn(
        "fixed bottom-4 start-4 z-[80] flex max-w-xs items-start gap-3 rounded-md border border-line bg-card p-3.5 shadow-lift",
        "motion-safe:animate-[slidein_.25s_ease-out]",
      )}
    >
      <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xs bg-primary/10 text-primary">
        <DownloadIcon />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-bold text-ink-1">{t("pwa.installTitle")}</div>
        <p className="mt-0.5 text-xs leading-relaxed text-ink-2">
          {iosHint ? t("pwa.iosInstallHint") : t("pwa.installDesc")}
        </p>
        {!iosHint && (
          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleInstall()}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white transition hover:opacity-90 motion-reduce:transition-none"
            >
              {t("pwa.install")}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-md px-2.5 py-1.5 text-xs font-semibold text-ink-2 hover:text-ink-1"
            >
              {t("pwa.dismiss")}
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t("common.close")}
        className="p-0.5 text-base leading-none text-ink-3 hover:text-ink-1"
      >
        ×
      </button>
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}
